import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { apiService } from '../../services/api';

const KATEGORIE_LABELS = {
  WEG_PROTOKOLL: 'WEG-Protokoll',
  TEILUNGSERKLAERUNG: 'Teilungserklärung',
  WIRTSCHAFTSPLAN: 'Wirtschaftsplan',
  HAUSGELDABRECHNUNG: 'Hausgeldabrechnung',
  SONSTIGES: 'Sonstiges',
};

export default function ObjektDokumenteDialog({ objekt, open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jahrFilter, setJahrFilter] = useState('ALLE');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!open || !objekt?.objektid) return;
    setJahrFilter('ALLE');
    setError('');
    setLoading(true);
    apiService.get(`/api/objekt-dokumente?objekt_id=${objekt.objektid}`)
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Dokumente konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, objekt?.objektid]);

  const jahre = useMemo(() => {
    const set = new Set(items.map((it) => it.jahr).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [items]);

  const filtered = useMemo(
    () => (jahrFilter === 'ALLE' ? items : items.filter((it) => String(it.jahr) === String(jahrFilter))),
    [items, jahrFilter]
  );

  const handleDelete = async (item) => {
    const confirmed = window.confirm(`Dokument "${item.dokument_titel}" wirklich vom Objekt entfernen?`);
    if (!confirmed) return;
    try {
      setDeletingId(item.dokument_id);
      await apiService.delete(`/api/objekt-dokumente/${item.dokument_id}`);
      setItems((prev) => prev.filter((it) => it.dokument_id !== item.dokument_id));
      setError('');
    } catch (err) {
      setError(err.message || 'Löschen fehlgeschlagen');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Dokumente — {objekt?.objektkuerzel || `${objekt?.objektstrasse || ''} ${objekt?.objekthausnummer || ''}`.trim()}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, mt: 1 }}>
          <TextField
            select
            size="small"
            label="Jahr"
            value={jahrFilter}
            onChange={(e) => setJahrFilter(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="ALLE">Alle Jahre</MenuItem>
            {jahre.map((jahr) => (
              <MenuItem key={jahr} value={String(jahr)}>{jahr}</MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} Dokument(e)
          </Typography>
        </Stack>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Alert severity="info">
            Keine Dokumente vorhanden. Dokumente werden über den Dokumenteneingang (Ziel „Objekt-Dokument") zugeordnet.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell>Jahr</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.dokument_id} hover>
                  <TableCell>{item.dokument_titel}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={KATEGORIE_LABELS[item.dokument_kategorie] || item.dokument_kategorie} />
                  </TableCell>
                  <TableCell>{item.jahr || '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        disabled={!item.preview_url}
                        onClick={() => window.open(item.preview_url, '_blank', 'noopener,noreferrer')}
                      >
                        Ansehen
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={deletingId === item.dokument_id}
                        onClick={() => handleDelete(item)}
                      >
                        {deletingId === item.dokument_id ? 'Lösche...' : 'Löschen'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
